import React, { useRef, useState, useEffect } from "react";
import { Select, Button, Tooltip, Layout } from "antd";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Settings, Download } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import FavoriteListComponent from '../Favourites';
import SeaFlashPDF from '../../Ownerpage/Charts/ChartPrint';

const { Header } = Layout;
const { Option } = Select;

const ChartHeader = ({
  reportType, topic, menuItems,
  shiplist, filteredProcessdata, setFilterProcessData,
  chartlist, allEvents, setFavouriteSetting,
  filterCollapsed, setFilterCollapsed,
  isGeneratingPDF, handleGeneratePDF,
  pdfData,
}) => {
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [menuItems]);

  const scroll = (dir) =>
    tabsRef.current?.scrollBy({ left: dir * 220, behavior: "smooth" });

  // ── Reusable arrow button ──────────────────────────────────────────────
  const ArrowBtn = ({ dir, active }) => (
    <button
      onClick={() => active && scroll(dir)}
      title={dir === -1 ? "Scroll left" : "Scroll right"}
      style={{
        flexShrink: 0, width: 26, height: 26,
        borderRadius: "50%",
        border: `1.5px solid ${active ? "#2F9F87" : "#e5e7eb"}`,
        background: active ? "#f0faf8" : "#f9fafb",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: active ? "pointer" : "default",
        opacity: active ? 1 : 0.35,
        transition: "all 0.18s",
        padding: 0,
        color: active ? "#2F9F87" : "#9ca3af",
        boxShadow: active ? "0 1px 4px rgba(47,159,135,0.18)" : "none",
      }}
      onMouseEnter={(e) => { if (active) e.currentTarget.style.background = "#ddf2ed"; }}
      onMouseLeave={(e) => { if (active) e.currentTarget.style.background = "#f0faf8"; }}
    >
      {dir === -1
        ? <ChevronLeft  size={13} strokeWidth={2.2} />
        : <ChevronRight size={13} strokeWidth={2.2} />}
    </button>
  );

  return (
   <Header
  className="shadow-sm border-bottom"
  style={{
    padding: "0 10px",
    backgroundColor: "#fff",
    height: 44,
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    lineHeight: "44px",
  }}
>
      {/* Report type selector */}
      <Select size="small" value={reportType} style={{ width: 130, flexShrink: 0 }}
        onChange={(e) => navigate(`/management/charts/${e}`)}>
        <Option value="DAILY">Daily Report</Option>
        <Option value="MONTHLY">Monthly Report</Option>
      </Select>

      {/* Divider */}
 
      {/* ── Arrow + scrollable tab strip ──────────────────────── */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", minWidth: 0, gap: 5 }}>

        <ArrowBtn dir={-1} active={canScrollLeft} />

        <div
          ref={tabsRef}
          className="chart-topic-tabs"
          style={{
            flex: 1, display: "flex", alignItems: "center",
            overflowX: "auto", overflowY: "hidden",
            scrollbarWidth: "none", msOverflowStyle: "none",
            minWidth: 0,
          }}
        >
          {menuItems.map((y) => {
            const active = y.label === topic;
            return (
              <NavLink key={y.label}
                to={`/management/charts/${reportType}/${y.label}`}
                style={{ textDecoration: "none", flexShrink: 0 }}
              >
                <div
                  style={{
                    padding: "0 14px", height: 44,
                    display: "flex", alignItems: "center",
                    fontSize: 13,
                    fontWeight: active ? 600 : 400,
                    color: active ? "#2F9F87" : "#4b5563",
                    borderBottom: `2px solid ${active ? "#2F9F87" : "transparent"}`,
                    background: active ? "#f0faf8" : "transparent",
                    cursor: "pointer", whiteSpace: "nowrap",
                    transition: "color 0.15s, background 0.15s, border-color 0.15s",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) { e.currentTarget.style.color = "#2F9F87"; e.currentTarget.style.background = "#f5faf9"; }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) { e.currentTarget.style.color = "#4b5563"; e.currentTarget.style.background = "transparent"; }
                  }}
                >
                  {y.label}
                </div>
              </NavLink>
            );
          })}
        </div>

        <ArrowBtn dir={1} active={canScrollRight} />
      </div>

      {/* ── Right controls ────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <Select size="small" style={{ width: 130 }} allowClear
          value={filteredProcessdata?.ship_id} placeholder="Select Ship"
          onChange={(e) => setFilterProcessData((prev) => ({ ...prev, ship_id: e }))}
          options={shiplist?.map((x) => ({ label: x?.name, value: x.imo_number }))}
        />

           <FavoriteListComponent chartGroups={chartlist} reportType={reportType}
            filterOptions={allEvents} setFavouriteSetting={setFavouriteSetting} />
     

        <Tooltip title="Filters">
          <Button size="small" type={filterCollapsed ? "primary" : "default"}
            icon={filterCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            onClick={() => setFilterCollapsed(!filterCollapsed)}>
            Filter
          </Button>
        </Tooltip>

        {!pdfData && (
          <Tooltip title="Generate Report">
            <Button size="small" type="primary" loading={isGeneratingPDF}
              icon={<Settings size={14} />} onClick={handleGeneratePDF}>
              Generate
            </Button>
          </Tooltip>
        )}

        {pdfData && (
          <PDFDownloadLink document={<SeaFlashPDF data={pdfData} />} fileName="seaflash_report.pdf">
            {({ loading }) => (
              <Tooltip title="Download Report">
                <Button size="small" type="default" disabled={loading} loading={isGeneratingPDF}
                  icon={loading ? <Settings size={14} /> : <Download size={14} />}>
                  {loading ? "Preparing..." : "Download"}
                </Button>
              </Tooltip>
            )}
          </PDFDownloadLink>
        )}
      </div>
    </Header>
  );
};

export default ChartHeader;